<template>
  <div>
    <div class="data-table-shell">
      <div class="data-table-scroll">
        <div v-if="loading" class="w-full">
          <table class="data-table">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">{{ text.orderInfo }}</th>
                <th v-if="showPlanColumn" class="data-table-th text-left">{{ text.plan }}</th>
                <th v-if="showCustomerColumn" class="data-table-th text-left">{{ text.customer }}</th>
                <th v-if="showCompanyColumn" class="data-table-th text-left">{{ text.company }}</th>
                <th class="data-table-th text-left">{{ text.owner }}</th>
                <th class="data-table-th text-left">{{ text.executor }}</th>
                <th class="data-table-th text-left">{{ text.servicePeriod }}</th>
                <th class="data-table-th is-right">{{ text.amount }}</th>
                <th class="data-table-th is-center">{{ text.status }}</th>
                <th class="data-table-th is-center">{{ text.createTime }}</th>
                <th class="data-table-th is-center">{{ text.action }}</th>
              </tr>
            </thead>
            <tbody class="data-table-body data-table-skeleton">
              <tr v-for="n in 5" :key="n" class="animate-pulse">
                <td class="data-table-td">
                  <div class="flex items-center gap-3">
                    <div class="h-10 w-10 rounded-lg bg-slate-200"></div>
                    <div>
                      <div class="mb-1 h-4 w-32 rounded bg-slate-200"></div>
                      <div class="h-3 w-24 rounded bg-slate-200"></div>
                    </div>
                  </div>
                </td>
                <td v-if="showPlanColumn" class="data-table-td"><div class="h-6 w-20 rounded bg-slate-200"></div></td>
                <td v-if="showCustomerColumn" class="data-table-td"><div class="h-4 w-20 rounded bg-slate-200"></div></td>
                <td v-if="showCompanyColumn" class="data-table-td"><div class="h-4 w-24 rounded bg-slate-200"></div></td>
                <td class="data-table-td">
                  <div class="flex items-center gap-2">
                    <div class="h-6 w-6 rounded-full bg-slate-200"></div>
                    <div class="h-4 w-16 rounded bg-slate-200"></div>
                  </div>
                </td>
                <td class="data-table-td"><div class="h-4 w-16 rounded bg-slate-200"></div></td>
                <td class="data-table-td"><div class="h-4 w-32 rounded bg-slate-200"></div></td>
                <td class="data-table-td is-right"><div class="ml-auto h-4 w-20 rounded bg-slate-200"></div></td>
                <td class="data-table-td is-center"><div class="mx-auto h-6 w-14 rounded-full bg-slate-200"></div></td>
                <td class="data-table-td is-center"><div class="mx-auto h-4 w-24 rounded bg-slate-200"></div></td>
                <td class="data-table-td">
                  <div class="flex items-center justify-center gap-2">
                    <div class="h-8 w-8 rounded bg-slate-200"></div>
                    <div class="h-8 w-8 rounded bg-slate-200"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="orders.length === 0" :class="[emptyStateClass, compactEmpty ? 'data-table-empty is-compact' : 'data-table-empty']">
          <template v-if="compactEmpty">
            {{ text.empty }}
          </template>
          <template v-else>
            <div class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100">
              <svg class="h-10 w-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
            </div>
            <h3 class="mb-2 text-lg font-medium text-slate-900">{{ text.empty }}</h3>
            <p class="text-slate-500">{{ emptyDescription }}</p>
          </template>
        </div>

        <table v-else class="data-table">
          <thead class="data-table-head">
            <tr>
              <th class="data-table-th text-left">{{ text.orderInfo }}</th>
              <th v-if="showPlanColumn" class="data-table-th text-left">{{ text.plan }}</th>
              <th v-if="showCustomerColumn" class="data-table-th text-left">{{ text.customer }}</th>
              <th v-if="showCompanyColumn" class="data-table-th text-left">{{ text.company }}</th>
              <th class="data-table-th text-left">{{ text.owner }}</th>
              <th class="data-table-th text-left">{{ text.executor }}</th>
              <th class="data-table-th text-left">{{ text.servicePeriod }}</th>
              <th class="data-table-th is-right">{{ text.amount }}</th>
              <th class="data-table-th is-center">{{ text.status }}</th>
              <th class="data-table-th is-center">{{ text.createTime }}</th>
              <th class="data-table-th is-center">{{ text.action }}</th>
            </tr>
          </thead>
          <tbody class="data-table-body">
            <tr v-for="order in orders" :key="order.id" class="data-table-row">
              <td class="data-table-td">
                <div class="flex items-center gap-3">
                  <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                    <svg class="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  </div>
                  <div>
                    <div class="data-table-cell-title">{{ order.productNameSnapshot }}</div>
                    <div class="data-table-cell-meta">{{ order.orderNo }}</div>
                  </div>
                </div>
              </td>

              <td v-if="showPlanColumn" class="data-table-td text-sm text-slate-600">
                <button
                  v-if="order.planName"
                  class="data-table-inline-tag is-clickable"
                  @click="emit('plan-click', order)"
                >
                  {{ order.planName }}
                </button>
                <span v-else class="data-table-inline-tag text-slate-500">-</span>
              </td>

              <td v-if="showCustomerColumn" class="data-table-td text-sm text-slate-600">{{ order.customerName || '-' }}</td>

              <td v-if="showCompanyColumn" class="data-table-td text-sm text-slate-600">{{ order.companyName || '-' }}</td>

              <td class="data-table-td text-sm text-slate-600">
                <div class="flex items-center gap-2">
                  <div class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-50">
                    <svg class="h-3 w-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                  </div>
                  <span>{{ order.ownerUserName || '-' }}</span>
                </div>
              </td>

              <td class="data-table-td text-sm text-slate-600">{{ order.executorName || '-' }}</td>

              <td class="data-table-td text-sm text-slate-600">{{ formatServicePeriod(order) }}</td>

              <td class="data-table-td is-right text-sm font-medium text-slate-900">&#165;{{ formatAmount(order.amount) }}</td>

              <td class="data-table-td is-center">
                <div class="flex flex-col items-center gap-1.5">
                  <span :class="['status-badge', getStatusClass(order.status)]">
                    {{ getStatusText(order.status) }}
                  </span>
                  <button
                    v-if="hasPendingApproval(order)"
                    type="button"
                    :class="['status-badge text-xs', getPendingApprovalStatusClass(order.pendingApprovalStatus)]"
                    :title="text.approvalDetail"
                    @click="emit('approval-detail', order)"
                  >
                    {{ getPendingApprovalStatusText(order.pendingApprovalStatus) }}
                  </button>
                </div>
              </td>

              <td class="data-table-td is-center text-sm text-slate-600">{{ $formatDateTime(order.createTime) }}</td>

              <td class="data-table-td">
                <div class="flex items-center justify-center gap-2">
                  <button
                    class="table-action-button is-primary"
                    :title="text.viewDetail"
                    @click="emit('detail', order)"
                  >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </button>

                  <template v-if="showApprovalActions && order.status === 0">
                    <el-popover
                      :visible="approvePopoverOrderId === order.id"
                      placement="left"
                      :width="240"
                    >
                      <template #reference>
                        <button
                          v-permission="approvePermission"
                          class="table-action-button is-success"
                          :title="text.approve"
                          @click="emit('open-approve-popover', order)"
                        >
                          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                          </svg>
                        </button>
                      </template>
                      <div class="space-y-3">
                        <div class="text-sm font-medium text-slate-700">{{ text.approveConfirm }}</div>
                        <div class="flex justify-end gap-2">
                          <button class="rounded-lg bg-slate-100 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200" @click="emit('close-approve-popover')">{{ text.cancel }}</button>
                          <button
                            :disabled="approvalSubmitting"
                            class="rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:opacity-50"
                            @click="emit('approve', { order, approved: true })"
                          >
                            {{ text.confirmApprove }}
                          </button>
                        </div>
                      </div>
                    </el-popover>

                    <el-popover
                      :visible="rejectPopoverOrderId === order.id"
                      placement="left"
                      :width="280"
                      trigger="click"
                    >
                      <template #reference>
                        <button
                          v-permission="approvePermission"
                          class="table-action-button is-danger"
                          :title="text.reject"
                          @click="emit('open-reject-popover', order)"
                        >
                          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                          </svg>
                        </button>
                      </template>
                      <div class="space-y-3">
                        <div class="text-sm font-medium text-slate-700">{{ text.rejectPrompt }}</div>
                        <el-input
                          :model-value="rejectReason"
                          type="textarea"
                          :rows="3"
                          :placeholder="text.rejectPlaceholder"
                          maxlength="200"
                          show-word-limit
                          @update:model-value="emit('update:reject-reason', $event)"
                        />
                        <div class="flex justify-end gap-2">
                          <button class="rounded-lg bg-slate-100 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200" @click="emit('close-reject-popover')">{{ text.cancel }}</button>
                          <button
                            :disabled="approvalSubmitting"
                            class="rounded-lg bg-red-600 px-3 py-1.5 text-sm text-white hover:bg-red-700 disabled:opacity-50"
                            @click="emit('approve', { order, approved: false })"
                          >
                            {{ text.confirmReject }}
                          </button>
                        </div>
                      </div>
                    </el-popover>
                  </template>

                  <button
                    v-permission="editPermission"
                    v-if="showEditAction && canEdit(order)"
                    class="table-action-button is-primary"
                    :title="text.edit"
                    @click="emit('edit', order)"
                  >
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>

                  <el-dropdown
                    v-permission="statusEditPermission"
                    v-if="showStatusAction && getAvailableStatusOptions(order.status).length > 0"
                    trigger="click"
                    @command="(status) => emit('status-change', { order, status })"
                  >
                    <button class="table-action-button is-neutral" :title="text.changeStatus">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                      </svg>
                    </button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <el-dropdown-item
                          v-for="status in getAvailableStatusOptions(order.status)"
                          :key="status.value"
                          :command="status.value"
                        >
                          {{ status.label }}
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <AppPagination
      v-if="orders.length > 0 && showPagination"
      :pagination="pagination"
      :page-sizes="[10, 20, 50]"
      :class="paginationClass"
      :total-label="`${text.total} `"
      :total-suffix="` ${text.item}`"
      :page-size-label="text.perPage"
      :page-size-suffix="text.item"
      :prev-text="text.prev"
      :next-text="text.next"
      @page-change="emit('page-change', $event)"
      @size-change="emit('size-change', $event)"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import AppPagination from '@/components/AppPagination.vue'

const text = {
  "orderInfo": "订单信息",
  "plan": "所属方案",
  "customer": "客户",
  "company": "客户企业",
  "owner": "销售人员",
  "executor": "执行人",
  "servicePeriod": "服务周期",
  "amount": "订单金额",
  "status": "状态",
  "createTime": "创建时间",
  "action": "操作",
  "empty": "暂无订单数据",
  "pending": "待确认",
  "executing": "执行中",
  "completed": "已完成",
  "cancelled": "已取消",
  "rejected": "已驳回",
  "unknown": "未知",
  "viewDetail": "查看详情",
  "approve": "通过",
  "reject": "不通过",
  "approveConfirm": "确认通过该订单？",
  "rejectPrompt": "请填写不通过原因",
  "rejectPlaceholder": "请输入原因",
  "amountApproval": "金额变更",
  "approvalDetail": "查看审批详情",
  "confirmApprove": "确认通过",
  "confirmReject": "确认拒绝",
  "cancel": "取消",
  "edit": "编辑",
  "changeStatus": "变更状态",
  "total": "共",
  "item": "条",
  "prev": "上一页",
  "next": "下一页",
  "perPage": "每页",
  "emptyDescription": "您可以在订单管理中继续跟踪服务执行。"
}

const statusOptions = [
  { label: text.pending, value: 0 },
  { label: text.executing, value: 1 },
  { label: text.completed, value: 2 },
  { label: text.cancelled, value: 3 },
  { label: text.rejected, value: 4 }
]

const statusMap = {
  0: { text: text.pending, class: 'bg-slate-100 text-slate-600' },
  1: { text: text.executing, class: 'bg-blue-100 text-blue-700' },
  2: { text: text.completed, class: 'bg-green-100 text-green-700' },
  3: { text: text.cancelled, class: 'bg-red-100 text-red-700' },
  4: { text: text.rejected, class: 'bg-orange-100 text-orange-700' }
}

const props = defineProps({
  orders: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  pagination: {
    type: Object,
    default: () => ({
      current: 1,
      size: 20,
      total: 0,
      pages: 0
    })
  },
  showPlanColumn: { type: Boolean, default: true },
  showCustomerColumn: { type: Boolean, default: true },
  showCompanyColumn: { type: Boolean, default: true },
  showApprovalActions: { type: Boolean, default: true },
  showEditAction: { type: Boolean, default: true },
  showStatusAction: { type: Boolean, default: true },
  showPagination: { type: Boolean, default: true },
  approvePermission: { type: [String, Array], default: 'order:approve' },
  editPermission: { type: [String, Array], default: 'order:edit' },
  statusEditPermission: { type: [String, Array], default: 'order:status:edit' },
  compactEmpty: { type: Boolean, default: false },
  emptyDescription: {
    type: String,
    default: '\u60a8\u53ef\u4ee5\u5728\u8ba2\u5355\u7ba1\u7406\u4e2d\u7ee7\u7eed\u8ddf\u8fdb\u670d\u52a1\u6267\u884c\u3002'
  },
  approvePopoverOrderId: { type: [String, Number], default: null },
  rejectPopoverOrderId: { type: [String, Number], default: null },
  rejectReason: { type: String, default: '' },
  approvalSubmitting: { type: Boolean, default: false }
})

const emit = defineEmits([
  'detail',
  'plan-click',
  'edit',
  'status-change',
  'page-change',
  'size-change',
  'open-approve-popover',
  'close-approve-popover',
  'open-reject-popover',
  'close-reject-popover',
  'update:reject-reason',
  'approve',
  'approval-detail'
])

const emptyStateClass = computed(() => {
  if (props.compactEmpty) {
    return 'rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-500'
  }

  return 'py-16 text-center'
})

const paginationClass = computed(() => {
  if (props.compactEmpty) {
    return 'pagination-bridge'
  }

  return 'pagination-bridge'
})

const getStatusClass = (status) => statusMap[status]?.class || 'bg-slate-100 text-slate-600'
const getStatusText = (status) => statusMap[status]?.text || text.unknown
const pendingApprovalStatusMap = {
  0: { text: '审批中', class: 'bg-amber-100 text-amber-700' },
  1: { text: '已通过', class: 'bg-green-100 text-green-700' },
  2: { text: '已驳回', class: 'bg-red-100 text-red-700' },
  3: { text: '已取消', class: 'bg-slate-100 text-slate-600' }
}
const hasPendingApproval = (order) => order?.pendingApprovalInstanceId != null && order?.pendingApprovalStatus != null
const getPendingApprovalStatusClass = (status) => pendingApprovalStatusMap[Number(status)]?.class || 'bg-slate-100 text-slate-600'
const getPendingApprovalStatusText = (status) => `${text.amountApproval}${pendingApprovalStatusMap[Number(status)]?.text || text.unknown}`
const formatAmount = (amount) => Number(amount || 0).toLocaleString()
const canEdit = (order) => order.status !== 2 && order.status !== 3

const formatServicePeriod = (order) => {
  const start = order?.serviceStart || '-'
  const end = order?.serviceEnd || '-'
  return `${start} ~ ${end}`
}

const getAvailableStatusOptions = (currentStatus) => {
  switch (currentStatus) {
    case 1:
      return statusOptions.filter(status => status.value === 2 || status.value === 3)
    case 3:
      return statusOptions.filter(status => status.value === 1)
    default:
      return []
  }
}
</script>
